import React, { useState, useMemo, useCallback, useEffect, useLayoutEffect } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import Switch from '@mui/material/Switch';
import DataTable, { ColDef, BulkEditField } from '../util/DataTable';
import UserAvatar from '../util/UserAvatar';
import SearchFilter, { applySearchAndFilters, ActiveFilter } from '../util/SearchFilter';
import TabMenu from '../util/TabMenu';

// ─── interfaces ───────────────────────────────────────────────────────────────

interface OrgUser {
  id: number;
  first_name: string;
  last_name: string;
  second_name?: string;
  job_title?: string;
  email?: string;
  mobile_phone?: string;
  city?: string;
  contract_start_date?: string | Date;
  pattern?: { id?: number; name?: string; hoursPerWeek?: number };
  role?: { id: number; title: string };
  roles?: { id: number; title: string }[];
  avatarUrl?: string;
  supervisorId?: number;
  departmentId?: number;
  is_active?: boolean;
  profile_completed?: boolean;
  workingStatusId?: number | null;
  workingStatus?: { id: number; name: string; color: string; emoji: string; slug?: string } | null;
  isManager?: boolean;
}

interface Dept {
  id: number;
  title: string;
  parentId?: number | null;
}

interface DeptMembership {
  userId: number;
  departmentId: number;
  role: string;
}

interface OrgTreeProps {
  users: OrgUser[];
  departments: Dept[];
  deptMemberships?: DeptMembership[];
  baseUrl: string;
  onUserClick?: (userId: number) => void;
  onUserDrop?: (userId: number, newDeptId: number | null) => void;
  onUserAssign?: (userId: number, deptId: number, role: string) => Promise<void>;
  onDeptsChange?: () => void;
  onUserAddToDept?: (userId: number, deptId: number) => Promise<void>;
  onAddNewUser?: (deptId: number) => void;
  onAddUser?: () => void;
  onAddNewUserToDept?: (deptId: number) => void;
  onBulkEdit?: (ids: (string | number)[], field: string, value: any) => Promise<void>;
  onDeleteMany?: (ids: (string | number)[]) => Promise<void>;
  /** Если задан — рендерит только этот вид без внутреннего TabMenu */
  forcedView?: 'tree' | 'cards' | 'list';
  /** Если false — скрывает переключатель Edit Mode (кнопка редактирования дерева) */
  canManage?: boolean;
}

interface UserNode {
  user: OrgUser;
  children: UserNode[];
}

interface DeptSection {
  deptId: number;
  deptTitle: string;
  color: string;
  managers: UserNode[];
  employees: UserNode[];
  subSections: DeptSection[];
}

// ─── colours ──────────────────────────────────────────────────────────────────

const DEPT_COLORS = [
  '#4f8ef7', '#43c98a', '#f76f8e', '#f4a942', '#a78bfa',
  '#22d3ee', '#fb923c', '#84cc16', '#e879f9', '#38bdf8',
];

const CONNECTOR = 'var(--text-color-calendar-week)';

function getDeptColor(deptId: number | undefined, departments: Dept[]): string {
  if (!deptId) return '#9ca3af';
  const idx = departments.findIndex((d) => d.id === deptId);
  return idx >= 0 ? DEPT_COLORS[idx % DEPT_COLORS.length] : '#9ca3af';
}

// ─── build helpers ────────────────────────────────────────────────────────────

function buildDeptSection(dept: Dept, allDepts: Dept[], allUsers: OrgUser[], memberships: DeptMembership[]): DeptSection {
  const color = getDeptColor(dept.id, allDepts);
  const deptMems = memberships.filter((m) => m.departmentId === dept.id);
  const deptUserIds = new Set(deptMems.map((m) => m.userId));
  const managers: UserNode[] = [];
  const employees: UserNode[] = [];
  deptMems.forEach((m) => {
    const user = allUsers.find((u) => u.id === m.userId);
    if (!user) return;
    const node: UserNode = { user, children: [] };
    if (m.role === 'head') managers.push(node);
    else employees.push(node);
  });
  // fallback: also include users with user.departmentId if no memberships loaded
  if (memberships.length === 0) {
    allUsers.filter((u) => u.departmentId === dept.id && !deptUserIds.has(u.id)).forEach((u) => {
      const node: UserNode = { user: u, children: [] };
      if (u.isManager) managers.push(node);
      else employees.push(node);
    });
  }
  const subSections = allDepts.filter((d) => d.parentId === dept.id)
    .map((d) => buildDeptSection(d, allDepts, allUsers, memberships));
  return { deptId: dept.id, deptTitle: dept.title, color, managers, employees, subSections };
}

function buildData(users: OrgUser[], departments: Dept[], memberships: DeptMembership[]) {
  if (!Array.isArray(users) || !Array.isArray(departments) || !Array.isArray(memberships)) {
    return { sections: [], unassigned: [] };
  }
  const rootDepts = departments.filter((d) => !d.parentId);
  const sections = rootDepts.map((d) => buildDeptSection(d, departments, users, memberships));
  const assignedUserIds = new Set(memberships.map((m) => m.userId));
  // fallback when no memberships: use departmentId
  const assignedDeptIds = new Set(departments.map((d) => d.id));
  const unassigned = users.filter((u) =>
    memberships.length > 0
      ? !assignedUserIds.has(u.id)
      : (!u.departmentId || !assignedDeptIds.has(u.departmentId))
  );
  return { sections, unassigned };
}


// ─── Ava ──────────────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status?: OrgUser['workingStatus'] }> = ({ status }) => {
  if (!status) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 9, fontWeight: 600, color: '#fff', background: status.color || '#9ca3af', borderRadius: 3, padding: '1px 5px', marginTop: 2, whiteSpace: 'nowrap', flexShrink: 0 }}>
      {status.emoji} {status.name}
    </span>
  );
};

const Ava: React.FC<{ user: OrgUser; size: number; color: string; baseUrl: string }> = ({ user, size, baseUrl }) => (
  <UserAvatar user={user} baseUrl={baseUrl} size={size} />
);

// ─── EmpRow ──────────────────────────────────────────────────────────────────

const EmpRow: React.FC<{
  node: UserNode; departments: Dept[]; baseUrl: string; isHead: boolean;
  expandedMap: Record<number, boolean>; toggleExpand: (id: number) => void;
  onUserClick?: (id: number) => void; editMode: boolean;
  onRemoveFromDept?: (userId: number) => void;
}> = ({ node, departments, baseUrl, isHead, expandedMap, toggleExpand, onUserClick, editMode, onRemoveFromDept }) => {
  const { user, children } = node;
  const color = getDeptColor(user.departmentId, departments);
  const open = expandedMap[user.id] !== false;
  return (
    <div>
      <div
        draggable={editMode}
        onDragStart={(e) => { e.dataTransfer.setData('userId', String(user.id)); e.dataTransfer.effectAllowed = 'move'; e.stopPropagation(); }}
        onClick={() => onUserClick?.(user.id)}
        onDoubleClick={() => onUserClick?.(user.id)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: isHead ? '6px 8px' : '4px 8px', borderRadius: 6, marginBottom: 2, background: isHead ? 'var(--bg-form)' : 'transparent', border: isHead ? '1px solid var(--border-input)' : 'none', cursor: onUserClick ? 'pointer' : editMode ? 'grab' : 'default', userSelect: 'none' }}
      >
        <Ava user={user} size={isHead ? 32 : 26} color={color} baseUrl={baseUrl} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: isHead ? 12 : 11, fontWeight: isHead ? 600 : 400, color: 'var(--text-color)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3 }}>{user.first_name} {user.last_name}</p>
          <p style={{ margin: 0, fontSize: 10, color: 'var(--text-color-calendar-week)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3 }}>{user.job_title || '—'}</p>
          <StatusBadge status={user.workingStatus} />
        </div>
        {children.length > 0 && (
          <button type="button" onClick={(e) => { e.stopPropagation(); toggleExpand(user.id); }} style={{ width: 16, height: 16, borderRadius: '50%', background: 'var(--btn-user-btn)', border: '1px solid var(--border-input)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-color)', fontSize: 12, padding: 0, flexShrink: 0, lineHeight: 1 }}>
            {open ? '−' : '+'}
          </button>
        )}
        {editMode && onRemoveFromDept && (
          <button type="button" title="Remove from dept" onClick={(e) => { e.stopPropagation(); onRemoveFromDept(user.id); }}
            style={{ width: 16, height: 16, borderRadius: '50%', background: 'transparent', border: '1px solid var(--border-input)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f76f8e', fontSize: 11, padding: 0, flexShrink: 0, lineHeight: 1, opacity: 0.7 }}>
            ×
          </button>
        )}
      </div>
      {children.length > 0 && open && (
        <div style={{ marginLeft: 16, paddingLeft: 8, borderLeft: '1px solid var(--border-input)', marginBottom: 2 }}>
          {children.map((ch) => <EmpRow key={ch.user.id} node={ch} departments={departments} baseUrl={baseUrl} isHead={false} expandedMap={expandedMap} toggleExpand={toggleExpand} onUserClick={onUserClick} editMode={editMode} onRemoveFromDept={onRemoveFromDept} />)}
        </div>
      )}
    </div>
  );
};

// ─── DeptBox ─────────────────────────────────────────────────────────────────

const DeptBox: React.FC<{
  section: DeptSection;
  allDepts: Dept[];
  allUsers: OrgUser[];
  baseUrl: string;
  expandedMap: Record<number, boolean>;
  toggleExpand: (id: number) => void;
  open: boolean;
  toggle: () => void;
  onUserClick?: (id: number) => void;
  onUserDrop?: (userId: number, deptId: number | null) => void;
  onUserAssign?: (userId: number, deptId: number, role: string) => Promise<void>;
  onDeptsChange?: () => void;
  onAddNewUserToDept?: (deptId: number) => void;
  editMode: boolean;
}> = ({ section, allDepts, allUsers, baseUrl, expandedMap, toggleExpand, open, toggle, onUserClick, onUserDrop, onUserAssign, onDeptsChange, onAddNewUserToDept, editMode }) => {
  const [dragOver, setDragOver] = useState(false);
  const [dragOverZone, setDragOverZone] = useState<'manager' | 'employee' | null>(null);
  const [editTitle, setEditTitle] = useState(section.deptTitle);
  const [localColor, setLocalColor] = useState(section.color);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState(false);
  const [pendingDrop, setPendingDrop] = useState<{ userId: number; x: number; y: number } | null>(null);
  const [pickerState, setPickerState] = useState<{ role: 'head' | 'member' } | null>(null);
  const [pickerSearch, setPickerSearch] = useState('');
  const [addMenu, setAddMenu] = useState<'head' | 'member' | 'dept' | null>(null);

  useEffect(() => {
    if (!pendingDrop) return;
    const close = () => setPendingDrop(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [pendingDrop]);

  useEffect(() => { setEditTitle(section.deptTitle); }, [section.deptTitle]);

  const hasManagers = section.managers.length > 0;
  const hasEmployees = section.employees.length > 0;
  const isEmpty = !hasManagers && !hasEmployees;

  const handleRename = async () => {
    const title = editTitle.trim();
    if (!title || title === section.deptTitle) return;
    try { await axios.put(`department/${section.deptId}`, { title }); onDeptsChange?.(); } catch { setEditTitle(section.deptTitle); }
  };
  const handleDelete = async () => {
    const hasUsers = allUsers.some((u) => u.departmentId === section.deptId);
    if (hasUsers) { setDeleteError(true); setTimeout(() => setDeleteError(false), 3000); return; }
    try { await axios.delete(`department/${section.deptId}`); onDeptsChange?.(); } catch {}
    setConfirmDelete(false);
  };
  const handleDeptDrop = async (droppedDeptId: number) => {
    if (droppedDeptId === section.deptId) return;
    const isDescendant = (id: number, ancestorId: number): boolean => {
      const d = allDepts.find((x) => x.id === id);
      if (!d || !d.parentId) return false;
      if (d.parentId === ancestorId) return true;
      return isDescendant(d.parentId, ancestorId);
    };
    if (isDescendant(section.deptId, droppedDeptId)) return;
    try { await axios.put(`department/${droppedDeptId}`, { parentId: section.deptId }); onDeptsChange?.(); } catch {}
  };

  return (
    <div
      onDragOver={(e) => { if (editMode) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setDragOver(false);
        if (!editMode) return;
        const userId = Number(e.dataTransfer.getData('userId'));
        const deptId = Number(e.dataTransfer.getData('deptId'));
        if (userId) {
          e.stopPropagation();
          setPendingDrop({ userId, x: e.clientX, y: e.clientY });
        } else if (deptId) handleDeptDrop(deptId);
      }}
      style={{ borderRadius: 10, border: dragOver ? `2px dashed ${localColor}` : `1px solid ${localColor}40`, borderTop: `3px solid ${localColor}`, background: dragOver ? `${localColor}0d` : 'var(--bg-form)', boxShadow: `0 2px 8px rgba(var(--shodow),0.07)`, width: 200, minWidth: 200, overflow: 'hidden', transition: 'border .1s, background .1s' }}
    >
      {/* header */}
      <div
        draggable={editMode}
        onDragStart={(e) => { e.dataTransfer.setData('deptId', String(section.deptId)); e.dataTransfer.effectAllowed = 'move'; e.stopPropagation(); }}
        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', background: localColor + '18', borderBottom: open ? `1px solid ${localColor}28` : 'none', position: 'relative', cursor: editMode ? 'grab' : 'default' }}>
        {editMode && (
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div role="button" tabIndex={0} title="Change color"
              onClick={() => setShowColorPicker(!showColorPicker)}
              onKeyDown={(e) => { if (e.key === 'Enter') setShowColorPicker(!showColorPicker); }}
              style={{ width: 9, height: 9, borderRadius: '50%', background: localColor, cursor: 'pointer', border: `1px solid ${localColor}88` }}
            />
            {showColorPicker && (
              <>
                <div onClick={() => setShowColorPicker(false)} style={{ position: 'fixed', inset: 0, zIndex: 19 }} />
                <div style={{ position: 'absolute', top: '130%', left: -4, zIndex: 20, background: 'var(--bg-form)', border: '1px solid var(--border-input)', borderRadius: 8, padding: 6, display: 'flex', gap: 4, boxShadow: '0 4px 12px rgba(var(--shodow),0.18)' }}>
                  {DEPT_COLORS.map((c) => (
                    <div key={c} role="button" tabIndex={0} title={c}
                      onClick={() => { setLocalColor(c); setShowColorPicker(false); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { setLocalColor(c); setShowColorPicker(false); } }}
                      style={{ width: 18, height: 18, borderRadius: 4, background: c, cursor: 'pointer', outline: c === localColor ? '2px solid var(--text-color)' : 'none', outlineOffset: 1 }}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        {confirmDelete ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flex: 1 }}>
            {deleteError ? (
              <span style={{ fontSize: 10, color: '#ef4444', flex: 1, lineHeight: 1.2 }}>{t('DeptHasUsers')}</span>
            ) : (
              <>
                <span style={{ fontSize: 11, color: 'var(--text-color)', flex: 1 }}>Delete?</span>
                <button type="button" onClick={handleDelete} style={{ background: '#ef4444', border: 'none', borderRadius: 4, padding: '1px 6px', cursor: 'pointer', color: '#fff', fontSize: 11 }}>Yes</button>
              </>
            )}
            <button type="button" onClick={() => { setConfirmDelete(false); setDeleteError(false); }} style={{ background: 'var(--btn-user-btn)', border: 'none', borderRadius: 4, padding: '1px 6px', cursor: 'pointer', color: 'var(--text-color)', fontSize: 11 }}>No</button>
          </div>
        ) : editMode ? (
          <>
            <input title="Department name" value={editTitle} onChange={(e) => setEditTitle(e.target.value)}
              onBlur={handleRename}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditTitle(section.deptTitle); }}
              style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-color)', padding: 0 }}
            />
            <button type="button" title="Delete" onClick={() => setConfirmDelete(true)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-color-calendar-week)', display: 'flex', flexShrink: 0 }}><DeleteIcon sx={{ fontSize: 12 }} /></button>
          </>
        ) : (
          <span onClick={toggle} style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-color)', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}>{section.deptTitle}</span>
        )}
        <span onClick={toggle} style={{ fontSize: 10, color: localColor, fontWeight: 600, cursor: 'pointer', flexShrink: 0, userSelect: 'none' }}>{open ? '∧' : '∨'}</span>
      </div>

      {/* body — always two zones: manager (top) + employees (bottom) */}
      {open && (
        <div style={{ position: 'relative' }}>
          {/* ── Manager zone — separate drop target ── */}
          <div
            onDragOver={(e) => { if (editMode) { e.preventDefault(); e.stopPropagation(); setDragOverZone('manager'); } }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverZone(null); }}
            onDrop={(e) => {
              e.preventDefault(); e.stopPropagation(); setDragOverZone(null); setDragOver(false);
              if (!editMode) return;
              const userId = Number(e.dataTransfer.getData('userId'));
              if (userId) onUserAssign?.(userId, section.deptId, 'head');
            }}
            style={{ padding: '6px 6px 5px', minHeight: 40, background: dragOverZone === 'manager' ? localColor + '20' : localColor + '08', border: dragOverZone === 'manager' ? `1px dashed ${localColor}` : '1px solid transparent', borderRadius: dragOverZone === 'manager' ? 4 : 0, transition: 'background .15s' }}>
            <p style={{ margin: '0 0 3px 4px', fontSize: 9, fontWeight: 700, color: localColor, textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.8 }}>{t('OrgManagers')}</p>
            {section.managers.map((n) => <EmpRow key={n.user.id} node={n} departments={allDepts} baseUrl={baseUrl} isHead expandedMap={expandedMap} toggleExpand={toggleExpand} onUserClick={onUserClick} editMode={editMode} onRemoveFromDept={editMode ? (uid) => onUserAssign?.(uid, section.deptId, 'remove') : undefined} />)}
            {section.managers.length === 0 && (
              <div style={{ fontSize: 10, color: localColor, textAlign: 'center', padding: '3px 0', opacity: 0.4 }}>
                {editMode ? '↓ drop here' : '—'}
              </div>
            )}
          </div>

          {/* ── Divider ── */}
          <div style={{ height: 1, background: `linear-gradient(to right, transparent, ${localColor}60, transparent)` }} />

          {/* ── Employees zone — separate drop target ── */}
          <div
            onDragOver={(e) => { if (editMode) { e.preventDefault(); e.stopPropagation(); setDragOverZone('employee'); } }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverZone(null); }}
            onDrop={(e) => {
              e.preventDefault(); e.stopPropagation(); setDragOverZone(null); setDragOver(false);
              if (!editMode) return;
              const userId = Number(e.dataTransfer.getData('userId'));
              if (userId) onUserAssign?.(userId, section.deptId, 'member');
            }}
            style={{ padding: '5px 6px 6px', minHeight: 40, background: dragOverZone === 'employee' ? 'var(--bg-input)' : 'transparent', border: dragOverZone === 'employee' ? '1px dashed var(--border-input)' : '1px solid transparent', borderRadius: dragOverZone === 'employee' ? 4 : 0, transition: 'background .15s' }}>
            <p style={{ margin: '0 0 3px 4px', fontSize: 9, fontWeight: 700, color: 'var(--text-color-calendar-week)', textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.7 }}>{t('OrgEmployees')}</p>
            {section.employees.map((n) => <EmpRow key={n.user.id} node={n} departments={allDepts} baseUrl={baseUrl} isHead expandedMap={expandedMap} toggleExpand={toggleExpand} onUserClick={onUserClick} editMode={editMode} onRemoveFromDept={editMode ? (uid) => onUserAssign?.(uid, section.deptId, 'remove') : undefined} />)}
            {section.employees.length === 0 && (
              <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', textAlign: 'center', padding: '3px 0', opacity: 0.4 }}>
                {editMode ? '↓ drop here' : '—'}
              </div>
            )}
          </div>

          {/* ── Single Add button at bottom ── */}
          {editMode && (
            <div style={{ padding: '4px 6px', position: 'relative' }}>
              <button type="button" onClick={() => setAddMenu(addMenu === 'dept' ? null : 'dept')}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, fontSize: 11, color: 'var(--text-color-calendar-week)', border: '1px dashed var(--border-input)', background: 'transparent', borderRadius: 5, padding: '3px 8px', cursor: 'pointer', opacity: 0.7 }}>
                <AddIcon sx={{ fontSize: 11 }} /> {t('Add')}
              </button>
              {addMenu === 'dept' && (
                <div style={{ position: 'absolute', bottom: '100%', left: 6, right: 6, zIndex: 100, background: 'var(--bg-form)', border: '1px solid var(--border-input)', borderRadius: 5, boxShadow: '0 4px 10px rgba(0,0,0,0.12)', overflow: 'hidden', marginBottom: 2 }}>
                  <button type="button" onClick={() => { setAddMenu(null); setPickerState({ role: 'member' }); setPickerSearch(''); }}
                    style={{ width: '100%', padding: '6px 10px', fontSize: 11, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-color)', textAlign: 'left' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--btn-user-in)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                    {t('SelectExisting')}
                  </button>
                  <button type="button" onClick={() => { setAddMenu(null); onAddNewUserToDept?.(section.deptId); }}
                    style={{ width: '100%', padding: '6px 10px', fontSize: 11, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-color)', textAlign: 'left' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--btn-user-in)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                    {t('AddNewUser')}
                  </button>
                </div>
              )}
            </div>
          )}
          {pendingDrop && (
            <div onClick={(e) => e.stopPropagation()} style={{ position: 'fixed', top: pendingDrop.y, left: pendingDrop.x, zIndex: 9999, background: 'var(--bg-form)', border: '1px solid var(--border-input)', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: 4, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 130 }}>
              <button type="button" onClick={() => { onUserDrop?.(pendingDrop.userId, section.deptId); setPendingDrop(null); }}
                style={{ background: 'var(--btn-user-btn)', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', color: 'var(--text-color)', fontSize: 12, textAlign: 'left' }}>
                {t('MoveHere')}
              </button>
              <button type="button" onClick={() => { onUserAssign?.(pendingDrop.userId, section.deptId, 'member'); setPendingDrop(null); }}
                style={{ background: 'var(--btn-user-btn)', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', color: 'var(--text-color)', fontSize: 12, textAlign: 'left' }}>
                {t('AddHere')}
              </button>
            </div>
          )}
          {pickerState && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999, background: 'var(--bg-form)', border: '1px solid var(--border-input)', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: 6 }}>
              <input autoFocus value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)}
                placeholder="Search..." style={{ width: '100%', fontSize: 11, padding: '3px 6px', background: 'var(--bg-input)', border: '1px solid var(--border-input)', borderRadius: 4, color: 'var(--text-color)', boxSizing: 'border-box', marginBottom: 4 }} />
              <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                {allUsers.filter((u) => {
                  const q = pickerSearch.toLowerCase();
                  return !q || `${u.first_name} ${u.last_name}`.toLowerCase().includes(q);
                }).map((u) => (
                  <div key={u.id} onClick={() => { onUserAssign?.(u.id, section.deptId, pickerState.role); setPickerState(null); }}
                    style={{ padding: '3px 6px', cursor: 'pointer', fontSize: 12, color: 'var(--text-color)', borderRadius: 4 }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--btn-user-in)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                    {u.first_name} {u.last_name}
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => setPickerState(null)} style={{ marginTop: 4, width: '100%', fontSize: 11, background: 'transparent', border: 'none', color: 'var(--text-color-calendar-week)', cursor: 'pointer' }}>✕</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── GhostDeptBox ────────────────────────────────────────────────────────────

const GhostDeptBox: React.FC<{
  onConfirm: (title: string) => Promise<void>;
  onCancel: () => void;
}> = ({ onConfirm, onCancel }) => {
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const val = title.trim();
    if (!val) return;
    setSaving(true);
    await onConfirm(val);
    setSaving(false);
  };

  return (
    <div style={{ width: 200, minWidth: 200, borderRadius: 10, border: '2px dashed var(--border-input)', borderTop: '3px solid #9ca3af', background: 'var(--bg-form)', opacity: 0.6, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', background: '#9ca3af18' }}>
        <input
          autoFocus
          disabled={saving}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') onCancel(); }}
          placeholder="Department name..."
          style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-color)', padding: 0 }}
        />
        <button type="button" title="Confirm" onClick={handleSave} disabled={saving}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#43c98a', display: 'flex', flexShrink: 0 }}>
          <CheckIcon sx={{ fontSize: 14 }} />
        </button>
        <button type="button" title="Cancel" onClick={onCancel} disabled={saving}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-color-calendar-week)', display: 'flex', flexShrink: 0 }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </button>
      </div>
      <div style={{ padding: '18px 10px', textAlign: 'center' }}>
        <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>...</span>
      </div>
    </div>
  );
};

// ─── DeptNode ─────────────────────────────────────────────────────────────────

const DeptNode: React.FC<{
  section: DeptSection;
  allDepts: Dept[];
  allUsers: OrgUser[];
  baseUrl: string;
  expandedMap: Record<number, boolean>;
  toggleExpand: (id: number) => void;
  openSections: Record<string, boolean>;
  toggleSection: (key: string) => void;
  onUserClick?: (id: number) => void;
  onUserDrop?: (userId: number, deptId: number | null) => void;
  onUserAssign?: (userId: number, deptId: number, role: string) => Promise<void>;
  onDeptsChange?: () => void;
  onUserAddToDept?: (userId: number, deptId: number) => Promise<void>;
  onAddNewUser?: (deptId: number) => void;
  onAddNewUserToDept?: (deptId: number) => void;
  editMode: boolean;
}> = (props) => {
  const { section, editMode, onDeptsChange, ...restProps } = props;
  const [addingSub, setAddingSub] = useState(false);
  const key = String(section.deptId);
  const open = props.openSections[key] !== false;
  const toggle = () => props.toggleSection(key);
  const hasSubs = section.subSections.length > 0;
  const showHRow = hasSubs || addingSub;

  const handleAddSub = async (title: string) => {
    try { await axios.post('department/add', { title, parentId: section.deptId }); onDeptsChange?.(); } catch {}
    setAddingSub(false);
  };

  const box = (
    <DeptBox
      section={section}
      allDepts={restProps.allDepts}
      allUsers={restProps.allUsers}
      baseUrl={restProps.baseUrl}
      expandedMap={restProps.expandedMap}
      toggleExpand={restProps.toggleExpand}
      open={open}
      toggle={toggle}
      onUserClick={restProps.onUserClick}
      onUserDrop={restProps.onUserDrop}
      onUserAssign={restProps.onUserAssign}
      onDeptsChange={onDeptsChange}
      onAddNewUserToDept={restProps.onAddNewUserToDept}
      editMode={editMode}
    />
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {box}
      {/* sub-dept "+" button — 5px below the block, only when not already adding */}
      {editMode && !addingSub && (
        <button type="button" title="Add sub-department"
          onClick={() => setAddingSub(true)}
          style={{ marginTop: 5, width: 18, height: 18, borderRadius: '50%', background: 'var(--btn-user-in)', border: '1px dashed var(--border-input)', cursor: 'pointer', color: 'var(--text-color-calendar-week)', fontSize: 14, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0 }}>
          +
        </button>
      )}
      {showHRow && (
        <>
          <div style={{ width: 2, height: 20, background: CONNECTOR, marginTop: addingSub ? 5 : 0 }} />
          <HRow keys={[...section.subSections.map((s) => String(s.deptId)), ...(addingSub ? ['ghost-sub'] : [])]}>
            {section.subSections.map((sub) => (
              <DeptNode key={sub.deptId} section={sub} editMode={editMode} onDeptsChange={onDeptsChange} {...restProps} />
            ))}
            {addingSub && (
              <GhostDeptBox onConfirm={handleAddSub} onCancel={() => setAddingSub(false)} />
            )}
          </HRow>
        </>
      )}
    </div>
  );
};

// ─── HRow ────────────────────────────────────────────────────────────────────

const HRow: React.FC<{ children: React.ReactNode; keys: string[] }> = ({ children, keys }) => {
  const arr = React.Children.toArray(children);
  const count = arr.length;
  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}>
      {arr.map((child, idx) => {
        const isFirst = idx === 0, isLast = idx === count - 1, isOnly = count === 1;
        return (
          <div key={keys[idx]} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingLeft: 12, paddingRight: 12, position: 'relative' }}>
            {!isOnly && !isFirst && <div style={{ position: 'absolute', top: 0, left: 0, width: '50%', height: 2, background: CONNECTOR }} />}
            {!isOnly && !isLast && <div style={{ position: 'absolute', top: 0, left: '50%', right: 0, height: 2, background: CONNECTOR }} />}
            <div style={{ width: 2, height: 20, background: CONNECTOR }} />
            {child}
          </div>
        );
      })}
    </div>
  );
};

// ─── CardsView ───────────────────────────────────────────────────────────────

export const CardsView: React.FC<{
  users: OrgUser[];
  departments: Dept[];
  baseUrl: string;
  editMode: boolean;
  onUserClick?: (id: number) => void;
  onUserDrop?: (userId: number, deptId: number | null) => void;
}> = ({ users, departments, baseUrl, editMode, onUserClick, onUserDrop }) => {
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});
  const [dragOver, setDragOver] = useState<number | null>(null);
  const toggle = (id: number) => setCollapsed((p) => ({ ...p, [id]: !p[id] }));

  return (
    <div style={{ padding: '8px 0' }}>
      {departments.map((dept) => {
        const color = getDeptColor(dept.id, departments);
        const deptUsers = users.filter((u) => u.departmentId === dept.id);
        const isOpen = !collapsed[dept.id];
        const isOver = dragOver === dept.id;
        return (
          <div key={dept.id}
            onDragOver={(e) => { if (editMode) { e.preventDefault(); setDragOver(dept.id); } }}
            onDragLeave={(e) => { if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setDragOver(null); }}
            onDrop={(e) => {
              e.preventDefault(); setDragOver(null);
              if (!editMode) return;
              const userId = Number(e.dataTransfer.getData('userId'));
              if (userId) onUserDrop?.(userId, dept.id);
            }}
            style={{ borderBottom: `1px solid var(--border-input)`, background: isOver ? `${color}0d` : 'transparent', transition: 'background 0.1s', outline: isOver ? `2px dashed ${color}` : 'none', outlineOffset: -2 }}>
            <div onClick={() => toggle(dept.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px', cursor: 'pointer', userSelect: 'none' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-color)' }}>{dept.title}</span>
              <span style={{ fontSize: 12, color: 'var(--text-color-calendar-week)' }}>({deptUsers.length})</span>
              <span style={{ marginLeft: 'auto', fontSize: 13, color, fontWeight: 600 }}>{isOpen ? '∨' : '∧'}</span>
            </div>
            {isOpen && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, padding: '4px 20px 16px', minHeight: editMode ? 52 : undefined }}>
                {deptUsers.length === 0 && !isOver && (
                  <span style={{ fontSize: 12, color: 'var(--text-color-calendar-week)', padding: '4px 0' }}>—</span>
                )}
                {deptUsers.map((user) => {
                  const ucolor = getDeptColor(user.departmentId, departments);
                  return (
                    <div key={user.id}
                      draggable={editMode}
                      onDragStart={(e) => { e.dataTransfer.setData('userId', String(user.id)); e.dataTransfer.effectAllowed = 'move'; e.stopPropagation(); }}
                      onClick={() => onUserClick?.(user.id)}
                      onDoubleClick={() => onUserClick?.(user.id)}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--btn-user-form)', border: '1px solid var(--border-input)', borderRadius: 8, cursor: editMode ? 'grab' : onUserClick ? 'pointer' : 'default', width: 260, flexShrink: 0, boxShadow: '0 1px 4px rgba(var(--shodow),0.06)', userSelect: 'none' }}>
                      <Ava user={user} size={44} color={ucolor} baseUrl={baseUrl} />
                      <div style={{ minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--text-color)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.first_name} {user.last_name}</p>
                        <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--text-color-calendar-week)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.job_title || '—'}</p>
                        <StatusBadge status={user.workingStatus} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── UnassignedBar ────────────────────────────────────────────────────────────

export const UnassignedBar: React.FC<{ users: OrgUser[]; baseUrl: string; editMode: boolean; onUserClick?: (id: number) => void }> = ({ users, baseUrl, editMode, onUserClick }) => {
  if (users.length === 0) return null;
  return (
    <div className="bg-[var(--bg-form)] rounded-[5px] mt-[8px]">
      <div className="bg-[var(--bg-top)] px-[15px] py-[8px] rounded-t-[5px] flex items-center gap-[8px]">
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#9ca3af', display: 'inline-block' }} />
        <h3 className="text-[14px] font-semibold">{t('NoDepartment')}</h3>
        <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>({users.length})</span>
        {editMode && <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginLeft: 4 }}>— drag to assign</span>}
      </div>
      <div style={{ padding: '10px 14px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {users.map((user) => (
          <div key={user.id}
            draggable={editMode}
            onDragStart={(e) => { e.dataTransfer.setData('userId', String(user.id)); e.dataTransfer.effectAllowed = 'move'; }}
            onClick={() => onUserClick?.(user.id)}
            onDoubleClick={() => onUserClick?.(user.id)}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'var(--btn-user-form)', border: '1px solid var(--border-input)', borderRadius: 8, cursor: onUserClick ? 'pointer' : editMode ? 'grab' : 'default', userSelect: 'none', width: 170, flexShrink: 0, boxShadow: '0 1px 4px rgba(var(--shodow),0.06)' }}
          >
            <Ava user={user} size={30} color="#9ca3af" baseUrl={baseUrl} />
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: 'var(--text-color)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.first_name} {user.last_name}</p>
              <p style={{ margin: 0, fontSize: 10, color: 'var(--text-color-calendar-week)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.job_title || '—'}</p>
              <StatusBadge status={user.workingStatus} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── InactiveBar ──────────────────────────────────────────────────────────────

const InactiveBar: React.FC<{ users: OrgUser[]; baseUrl: string; onUserClick?: (id: number) => void }> = ({ users, baseUrl, onUserClick }) => {
  const [open, setOpen] = useState(false);
  if (users.length === 0) return null;
  return (
    <div className="bg-[var(--bg-form)] rounded-[5px] mt-[8px]">
      <div className="bg-[var(--bg-top)] px-[15px] py-[8px] rounded-t-[5px] flex items-center gap-[8px] cursor-pointer"
        onClick={() => setOpen((p) => !p)}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#9ca3af', display: 'inline-block', opacity: 0.5 }} />
        <h3 className="text-[14px] font-semibold" style={{ opacity: 0.7 }}>{t('Inactive')}</h3>
        <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>({users.length})</span>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-color-calendar-week)' }}>{open ? '∨' : '∧'}</span>
      </div>
      {open && (
        <div style={{ padding: '10px 14px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {users.map((user) => (
            <div key={user.id} onClick={() => onUserClick?.(user.id)} onDoubleClick={() => onUserClick?.(user.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'var(--btn-user-form)', border: '1px solid var(--border-input)', borderRadius: 8, cursor: onUserClick ? 'pointer' : 'default', userSelect: 'none', width: 170, flexShrink: 0, opacity: 0.65 }}>
              <Ava user={user} size={30} color="#9ca3af" baseUrl={baseUrl} />
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: 'var(--text-color)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.first_name} {user.last_name}</p>
                <p style={{ margin: 0, fontSize: 10, color: 'var(--text-color-calendar-week)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.job_title || '—'}</p>
                <StatusBadge status={user.workingStatus} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main OrgTree ─────────────────────────────────────────────────────────────

const OrgTree: React.FC<OrgTreeProps> = ({ users, departments, deptMemberships = [], baseUrl, onUserClick, onUserDrop, onUserAssign, onDeptsChange, onUserAddToDept, onAddNewUser, onAddUser, onBulkEdit, onDeleteMany, onAddNewUserToDept, forcedView, canManage = true }) => {
  const [editMode, setEditMode] = useState(false);
  const [viewMode, setViewMode] = useState<'tree' | 'cards' | 'list'>('tree');
  const effectiveView = forcedView ?? viewMode;
  const [manualZoom, setManualZoom] = useState<number | null>(null); // null = auto-fit
  const [autoZoom, setAutoZoom] = useState(1);
  const treeWrapRef = React.useRef<HTMLDivElement>(null);
  const treeContentRef = React.useRef<HTMLDivElement>(null);
  const effectiveZoom = manualZoom ?? autoZoom;
  const [expandedMap, setExpandedMap] = useState<Record<number, boolean>>({});
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  const inactiveUsers = useMemo(() => users.filter((u) => u.is_active === false), [users]);

  const activeUsers = useMemo(() => users.filter((u) => u.is_active !== false), [users]);

  const { sections, unassigned } = useMemo(() => buildData(activeUsers, departments, deptMemberships), [activeUsers, departments, deptMemberships]);

  const toggleExpand = useCallback((id: number) => {
    setExpandedMap((p) => ({ ...p, [id]: !p[id] }));
  }, []);

  const toggleSection = useCallback((key: string) => {
    setOpenSections((p) => ({ ...p, [key]: !p[key] }));
  }, []);

  const reset = () => { setExpandedMap({}); setOpenSections({}); setManualZoom(null); };

  const calcAutoZoom = useCallback(() => {
    const wrap = treeWrapRef.current;
    const content = treeContentRef.current;
    if (!wrap || !content) return;
    const style = content.style as CSSStyleDeclaration & { zoom: string };
    const prevZoom = style.zoom;
    style.zoom = '1';
    const naturalW = content.scrollWidth;
    style.zoom = prevZoom;
    const wrapW = wrap.clientWidth - 48;
    if (naturalW > 0 && wrapW > 0) {
      const fit = parseFloat(Math.min(1, Math.max(0.3, wrapW / naturalW)).toFixed(2));
      setAutoZoom((prev) => (Math.abs(fit - prev) > 0.01 ? fit : prev));
    }
  }, []);

  useLayoutEffect(() => {
    if (manualZoom !== null) return;
    calcAutoZoom();
  }, [sections, manualZoom, calcAutoZoom]);

  // пересчитываем при ресайзе окна (только в auto-режиме)
  // window.resize не срабатывает при изменении zoom внутри элемента — нет петли
  useEffect(() => {
    if (manualZoom !== null || viewMode !== 'tree') return;
    window.addEventListener('resize', calcAutoZoom);
    return () => window.removeEventListener('resize', calcAutoZoom);
  }, [manualZoom, viewMode, calcAutoZoom]);

  const [addingRoot, setAddingRoot] = useState(false);
  const [listSearch, setListSearch] = useState('');
  const [listFilters, setListFilters] = useState<ActiveFilter[]>([]);

  const handleCreateRootDept = async (title: string) => {
    try { await axios.post('department/add', { title }); onDeptsChange?.(); } catch {}
    setAddingRoot(false);
  };

  const uniqueDepts = useMemo(() => new Set(activeUsers.map((u) => u.departmentId).filter(Boolean)).size, [activeUsers]);
  const totalManagers = useMemo(() => activeUsers.filter((u) => activeUsers.some((o) => o.supervisorId === u.id)).length, [activeUsers]);
  const bulkEditFields = useMemo<BulkEditField[]>(() => [
    {
      key: 'is_active',
      label: t('Status'),
      type: 'select',
      options: [
        { value: true, label: t('OrgStatusActive') },
        { value: false, label: t('OrgStatusInactive') },
      ],
    },
    {
      key: 'departmentId',
      label: t('Department'),
      type: 'select',
      options: [
        { value: '', label: '—' },
        ...departments.map((d) => ({ value: d.id, label: d.title })),
      ],
    },
    {
      key: 'job_title',
      label: t('JobTitle'),
      type: 'text',
    },
  ], [departments]);

  const handleUserAssign = async (userId: number, deptId: number, role: string) => {
    if (onUserAssign) {
      await onUserAssign(userId, deptId, role);
    } else {
      try {
        const update: any = { departmentId: deptId };
        if (role === 'head') update.supervisorId = null;
        await axios.put(`update-user/${userId}`, update);
        onDeptsChange?.();
      } catch {}
    }
  };

  const nodeProps = { allDepts: departments, allUsers: users, baseUrl, expandedMap, toggleExpand, openSections, toggleSection, onUserClick, onUserDrop: editMode ? onUserDrop : undefined, onUserAssign: editMode ? handleUserAssign : undefined, onDeptsChange, onUserAddToDept, onAddNewUser, onAddNewUserToDept, editMode };

  const listColumns = useMemo<ColDef<OrgUser>[]>(() => [
    {
      key: 'name', label: t('Name'),
      render: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Ava user={row} size={28} color={getDeptColor(row.departmentId, departments)} baseUrl={baseUrl} />
          <span>{row.first_name} {row.last_name}</span>
        </div>
      ),
      filterType: 'text',
      filterExtract: (row) => `${row.first_name} ${row.last_name}`,
      sortExtract: (row) => `${row.first_name} ${row.last_name}`,
    },
    {
      key: 'job_title', label: t('JobTitle'),
      render: (row) => row.job_title || '—',
      filterType: 'text',
      filterExtract: (row) => row.job_title || '',
    },
    {
      key: 'department', label: t('Department'),
      render: (row) => {
        const dept = departments.find((d) => d.id === row.departmentId);
        return dept ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: getDeptColor(row.departmentId, departments), flexShrink: 0, display: 'inline-block' }} />
            {dept.title}
          </span>
        ) : '—';
      },
      filterType: 'select',
      filterOptions: departments.map((d) => ({ value: String(d.id), label: d.title })),
      filterExtract: (row) => row.departmentId ? String(row.departmentId) : '',
      sortExtract: (row) => departments.find((d) => d.id === row.departmentId)?.title || '',
    },
    {
      key: 'supervisor', label: t('Supervisor'),
      render: (row) => {
        if (!row.supervisorId) return '—';
        const sup = users.find((u) => u.id === row.supervisorId);
        return sup ? `${sup.first_name} ${sup.last_name}` : '—';
      },
      filterType: 'text',
      filterExtract: (row) => {
        if (!row.supervisorId) return '';
        const sup = users.find((u) => u.id === row.supervisorId);
        return sup ? `${sup.first_name} ${sup.last_name}` : '';
      },
      sortExtract: (row) => {
        if (!row.supervisorId) return '';
        const sup = users.find((u) => u.id === row.supervisorId);
        return sup ? `${sup.first_name} ${sup.last_name}` : '';
      },
    },
    {
      key: 'status', label: t('Status'),
      render: (row) => row.is_active === false
        ? <span style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af' }}>{t('OrgStatusInactive')}</span>
        : row.profile_completed === false
          ? <span style={{ fontSize: 11, fontWeight: 600, color: '#b45309' }}>Invite pending</span>
          : <span style={{ fontSize: 11, fontWeight: 600, color: '#43c98a' }}>{t('OrgStatusActive')}</span>,
      filterType: 'select',
      filterOptions: [
        { value: 'active', label: t('OrgStatusActive') },
        { value: 'inactive', label: t('OrgStatusInactive') },
        { value: 'pending', label: 'Invite pending' },
      ],
      filterExtract: (row) => row.is_active === false ? 'inactive' : row.profile_completed === false ? 'pending' : 'active',
      sortExtract: (row) => row.is_active === false ? 'inactive' : row.profile_completed === false ? 'pending' : 'active',
    },
    {
      key: 'email', label: t('Email'),
      render: (row) => row.email || '—',
      filterType: 'text',
      filterExtract: (row) => row.email || '',
      sortExtract: (row) => row.email || '',
    },
    {
      key: 'mobile_phone', label: t('MobilePhone'),
      render: (row) => row.mobile_phone || '—',
      filterType: 'text',
      filterExtract: (row) => row.mobile_phone || '',
      defaultVisible: false,
    },
    {
      key: 'role', label: t('Role'),
      render: (row) => row.role?.title || row.roles?.[0]?.title || '—',
      filterType: 'text',
      filterExtract: (row) => row.role?.title || row.roles?.[0]?.title || '',
      sortExtract: (row) => row.role?.title || row.roles?.[0]?.title || '',
      defaultVisible: false,
    },
    {
      key: 'contract_start_date', label: t('ContractStartDate'),
      render: (row) => row.contract_start_date ? String(row.contract_start_date).slice(0, 10) : '—',
      sortExtract: (row) => row.contract_start_date ? String(row.contract_start_date).slice(0, 10) : '',
      defaultVisible: false,
    },
    {
      key: 'hoursPerWeek', label: t('HoursPerWeek'),
      render: (row) => row.pattern?.hoursPerWeek != null ? String(row.pattern.hoursPerWeek) : '—',
      sortExtract: (row) => row.pattern?.hoursPerWeek != null ? String(row.pattern.hoursPerWeek).padStart(4, '0') : '',
      defaultVisible: false,
    },
    {
      key: 'city', label: t('City'),
      render: (row) => row.city || '—',
      filterType: 'text',
      filterExtract: (row) => row.city || '',
      sortExtract: (row) => row.city || '',
      defaultVisible: false,
    },
    {
      key: 'second_name', label: t('MiddleName'),
      render: (row) => row.second_name || '—',
      filterType: 'text',
      filterExtract: (row) => row.second_name || '',
      sortExtract: (row) => row.second_name || '',
      defaultVisible: false,
    },
  ], [users, departments, baseUrl]);

  const filteredListRows = applySearchAndFilters(users, listSearch, listFilters, listColumns,
    (u: any) => [`${u.first_name} ${u.last_name}`, u.second_name, u.job_title, u.email, u.mobile_phone, u.city].filter(Boolean).join(' '));

  return (
    <>
      <div className="bg-[var(--bg-form)] rounded-[5px] mb-[8px]">
        {/* Toolbar: Edit Mode + Zoom (только для tree/cards вида) */}
        {canManage && effectiveView !== 'list' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderBottom: '1px solid var(--border-input)', background: editMode ? 'rgba(79,142,247,0.06)' : undefined, transition: 'background .2s' }}>
            <span style={{ fontSize: 11, color: editMode ? '#4f8ef7' : 'var(--text-color-calendar-week)', fontWeight: editMode ? 700 : 400, transition: 'color .2s' }}>{t('EditMode')}</span>
            <Switch size="small" checked={editMode} onChange={(_, v) => setEditMode(v)}
              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4f8ef7' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4f8ef7' } }}
            />
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <button type="button" title="Zoom out" onClick={() => setManualZoom((z) => parseFloat(Math.max(0.2, (z ?? autoZoom) - 0.1).toFixed(2)))}
                style={{ width: 24, height: 24, borderRadius: 6, background: 'var(--btn-user-in)', border: '1px solid var(--border-input)', cursor: 'pointer', color: 'var(--text-color)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ZoomOutIcon sx={{ fontSize: 14 }} />
              </button>
              <button type="button" title="Reset zoom" onClick={() => { setManualZoom(null); setTimeout(calcAutoZoom, 0); }}
                style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-color)', background: 'var(--btn-user-in)', border: '1px solid var(--border-input)', borderRadius: 6, cursor: 'pointer', minWidth: 40, height: 24, textAlign: 'center', padding: '0 4px', fontFamily: 'monospace' }}>
                {manualZoom !== null ? `${Math.round(effectiveZoom * 100)}%` : 'auto'}
              </button>
              <button type="button" title="Zoom in" onClick={() => setManualZoom((z) => parseFloat(Math.min(2, (z ?? autoZoom) + 0.1).toFixed(2)))}
                style={{ width: 24, height: 24, borderRadius: 6, background: 'var(--btn-user-in)', border: '1px solid var(--border-input)', cursor: 'pointer', color: 'var(--text-color)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ZoomInIcon sx={{ fontSize: 14 }} />
              </button>
            </div>
          </div>
        )}
        {/* content */}
            {effectiveView === 'list' ? (
              <DataTable
                rows={filteredListRows}
                getKey={(row) => row.id}
                columns={listColumns}
                filterable={false}
                onEdit={onUserClick ? (row) => onUserClick(row.id) : undefined}
                bulkEditFields={onBulkEdit ? bulkEditFields : undefined}
                onBulkEdit={onBulkEdit}
                onDeleteMany={onDeleteMany}
                storageKey="org-list-view"
                defaultPageSize={25}
                topLeft={
                  <>
                    {onAddUser && (
                      <button type="button" onClick={onAddUser}
                        className="btn-primary">
                        <AddIcon sx={{ fontSize: 15 }} />
                        <span>{t('AddNewUser')}</span>
                      </button>
                    )}
                    <SearchFilter grow columns={listColumns} search={listSearch} onSearch={setListSearch} filters={listFilters} onFilters={setListFilters} />
                  </>
                }
              />
            ) : effectiveView === 'cards' ? (
              <CardsView users={activeUsers} departments={departments} baseUrl={baseUrl} editMode={editMode} onUserClick={onUserClick} onUserDrop={editMode ? onUserDrop : undefined} />
            ) : (
              <div ref={treeWrapRef} style={{ overflowX: 'auto', overflowY: 'auto', padding: '30px 24px', minHeight: 120, position: 'relative', display: 'flex', justifyContent: 'center' }}>
                {!editMode && sections.length === 0 && unassigned.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '4px 0' }}>
                    {unassigned.map((user) => (
                      <div key={user.id}
                        onClick={() => onUserClick?.(user.id)}
                        onDoubleClick={() => onUserClick?.(user.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'var(--btn-user-form)', border: '1px solid var(--border-input)', borderRadius: 8, cursor: onUserClick ? 'pointer' : 'default', userSelect: 'none', width: 170, flexShrink: 0 }}
                      >
                        <Ava user={user} size={30} color="#9ca3af" baseUrl={baseUrl} />
                        <div style={{ minWidth: 0 }}>
                          <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: 'var(--text-color)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.first_name} {user.last_name}</p>
                          <p style={{ margin: 0, fontSize: 10, color: 'var(--text-color-calendar-week)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.job_title || '—'}</p>
                          <StatusBadge status={user.workingStatus} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : !editMode && sections.length === 0 ? (
                  <div style={{ textAlign: 'center', opacity: 0.4, fontSize: 13, padding: '40px 0' }}>—</div>
                ) : (
                  <div ref={treeContentRef} style={{ display: 'block', width: 'fit-content', zoom: effectiveZoom }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      {editMode && !addingRoot && sections.length === 0 && (
                        <button type="button" onClick={() => setAddingRoot(true)}
                          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '24px 32px', background: 'var(--btn-user-form)', border: '2px dashed var(--border-input)', borderRadius: 10, cursor: 'pointer', color: 'var(--text-color-calendar-week)', marginBottom: 16 }}>
                          <span style={{ fontSize: 28, lineHeight: 1 }}>+</span>
                          <span style={{ fontSize: 12, fontWeight: 600 }}>{t('AddDepartment') || 'Add department'}</span>
                        </button>
                      )}
                      {editMode && !addingRoot && sections.length > 0 && (
                        <button type="button" title="Add department" onClick={() => setAddingRoot(true)}
                          style={{ width: 22, height: 22, marginBottom: 8, borderRadius: '50%', background: 'var(--btn-user-in)', border: '1px dashed var(--border-input)', cursor: 'pointer', color: 'var(--text-color-calendar-week)', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, lineHeight: 1, flexShrink: 0 }}>
                          +
                        </button>
                      )}
                      {(sections.length > 0 || addingRoot) && (
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 24, justifyContent: 'center', minWidth: 'max-content' }}>
                          {sections.map((section) => (
                            <DeptNode key={section.deptId} section={section} {...nodeProps} />
                          ))}
                          {addingRoot && (
                            <GhostDeptBox onConfirm={handleCreateRootDept} onCancel={() => setAddingRoot(false)} />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
      </div>

      {effectiveView !== 'list' && (
        <>
          <UnassignedBar users={unassigned} baseUrl={baseUrl} editMode={editMode} onUserClick={onUserClick} />
          <InactiveBar users={inactiveUsers} baseUrl={baseUrl} onUserClick={onUserClick} />
        </>
      )}
    </>
  );
};

export default OrgTree;
